import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { losApi } from './losApi';
import { downloadWithAuth } from './api';
import { ShieldCheck, AlertTriangle, CheckCircle2, Loader2, Download, Search } from 'lucide-react';
import PageContainer from './kit/PageContainer';
import DialogShell from './kit/DialogShell';
import { BRAND } from './kit/tokens';
import { errorMessage } from './errors';
import { toast } from '../hooks/use-toast';
import { useAuth } from './AuthContext';
import { isTenantAdminUser } from './RequireTenantAdmin';
import AuditLogTab from './AuditLogTab';
import PerformanceTab from './PerformanceTab';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { ChecksList, TRID_RULES } from './loan-file/shared/ChecksList';
import LoanStatusChip from './shared/LoanStatusChip';
import PickSelect from './shared/PickSelect';
import { Input } from '../components/ui/input';

const SEVERITY_COLOR = {
  critical: 'text-red-700 bg-red-50', high: 'text-red-600 bg-red-50',
  medium: 'text-amber-600 bg-amber-50', info: 'text-emerald-600 bg-emerald-50',
};

// Owners and admins also get the Performance roll-ups (ticket 193 D2, the server
// checks `reports.view`) and the workspace Audit log tab (D1, owners and admins
// only on the server too). Everyone else sees the dashboard alone.
export default function CompliancePage() {
  const auth = useAuth();
  const [tab, setTab] = useState('compliance');
  if (!isTenantAdminUser(auth?.user)) return <ComplianceDashboard />;
  return (
    <Tabs value={tab} onValueChange={setTab}>
      <div className="px-4 md:px-8 pt-4 md:pt-8">
        <TabsList>
          <TabsTrigger value="compliance" data-testid="reports-tab-compliance">Compliance</TabsTrigger>
          <TabsTrigger value="performance" data-testid="reports-tab-performance">Performance</TabsTrigger>
          <TabsTrigger value="audit" data-testid="reports-tab-audit">Audit log</TabsTrigger>
        </TabsList>
      </div>
      {/* Kept mounted so switching tabs does not re-run the dashboard over every loan. */}
      <TabsContent value="compliance" forceMount className="mt-0 data-[state=inactive]:hidden"><ComplianceDashboard /></TabsContent>
      <TabsContent value="performance" className="mt-0"><PerformanceTab /></TabsContent>
      <TabsContent value="audit" className="mt-0">
        <PageContainer title={<span className="inline-flex items-center gap-2"><ShieldCheck /> Audit log</span>} subtitle="Who did what in this workspace, newest first.">
          <AuditLogTab />
        </PageContainer>
      </TabsContent>
    </Tabs>
  );
}

const NEEDS_OPTIONS = [
  { value: '', label: 'All hot loans' },
  { value: 'fail', label: 'With failures' },
  { value: 'warn', label: 'With warnings' },
];
const NO_FILTERS = { needs: '', q: '' };

// Only the filters that are set go on the request.
const filterParams = ({ needs, q }) => ({ ...(needs ? { needs } : {}), ...(q ? { q } : {}) });

function ComplianceDashboard() {
  const [dash, setDash] = useState(null);
  const [hot, setHot] = useState([]);
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState(false);
  const [filters, setFilters] = useState(NO_FILTERS);
  const [search, setSearch] = useState('');
  const [filtering, setFiltering] = useState(false);
  const [exporting, setExporting] = useState(false);
  const request = useRef(0);
  const filtered = !!(filters.needs || filters.q);

  useEffect(() => {
    const mine = ++request.current;
    setFiltering(true);
    losApi.complianceDashboard(filterParams(filters))
      .then((d) => {
        if (request.current !== mine) return;
        setDash(d);
        setHot(d.hot_loans);
      })
      .catch((e) => {
        if (request.current !== mine) return;
        // A failed refresh keeps the page that is already showing.
        if (dash) toast({ title: "Couldn't apply the filter", description: errorMessage(e) });
        else setError(true);
      })
      .finally(() => { if (request.current === mine) setFiltering(false); });
    // `dash` is read only to choose between a toast and the full-page error.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters]);

  const applySearch = (e) => { e.preventDefault(); setFilters((f) => ({ ...f, q: search.trim() })); };
  const clearFilters = () => { setSearch(''); setFilters(NO_FILTERS); };

  const exportCsv = async () => {
    setExporting(true);
    try {
      await downloadWithAuth(losApi.hotLoansCsvPath(filters), 'hot_loans.csv');
    } finally {
      setExporting(false);
    }
  };

  const loadMore = async () => {
    const mine = request.current;
    setLoadingMore(true);
    try {
      const d = await losApi.complianceDashboard({ ...filterParams(filters), offset: hot.length });
      if (request.current !== mine) return;
      setHot((cur) => {
        const have = new Set(cur.map((l) => l.loan_file_id));
        return [...cur, ...d.hot_loans.filter((l) => !have.has(l.loan_file_id))];
      });
      setDash((cur) => ({ ...cur, hot_loans_total: d.hot_loans_total }));
    } catch (e) {
      toast({ title: "Couldn't load more loans", description: errorMessage(e) });
    } finally {
      setLoadingMore(false);
    }
  };

  if (error) return <div className="p-10 text-red-600 flex items-center gap-2"><AlertTriangle size={16} /> Couldn't load compliance. Refresh to try again.</div>;
  if (!dash) return <div className="p-10 text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading compliance…</div>;

  return (
    <PageContainer
      title={<span className="inline-flex items-center gap-2"><ShieldCheck /> Compliance</span>}
      subtitle="TRID timing, HMDA readiness, and firm-wide health."
    >
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Stat label="Total Loans" value={dash.total_loans} color={BRAND.navy} />
        <Stat label="Compliance Rate" value={`${dash.compliance_rate}%`} color={BRAND.accent} />
        <Stat label="With Failures" value={dash.loans_with_failures} color="#dc2626" />
        <Stat label="With Warnings" value={dash.loans_with_warnings} color="#f59e0b" />
      </div>

      <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between mb-3">
          <div className="font-serif font-semibold text-brand-navy">Hot Loans (need attention)</div>
          {(hot.length > 0 || filtered) && (
            <div className="flex flex-wrap items-center gap-2" data-testid="hot-loans-filters">
              <div className="w-44">
                <PickSelect label="Show" labelClassName="sr-only" value={filters.needs} onChange={(v) => setFilters((f) => ({ ...f, needs: v }))} options={NEEDS_OPTIONS} testId="hot-loans-needs" />
              </div>
              <form onSubmit={applySearch} className="flex items-center gap-1">
                <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Loan number, borrower or status" aria-label="Search hot loans" className="w-56" data-testid="hot-loans-search" />
                <button type="submit" className="h-9 px-2 rounded-md border border-slate-200" aria-label="Search" data-testid="hot-loans-search-apply"><Search size={14} /></button>
              </form>
              {filtered && <button type="button" onClick={clearFilters} className="text-xs font-semibold text-slate-600 underline" data-testid="hot-loans-clear">Clear</button>}
              <button type="button" onClick={exportCsv} disabled={exporting || hot.length === 0} className="h-9 px-3 rounded-md border border-slate-200 text-xs font-semibold inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="hot-loans-export">
                {exporting ? <Loader2 size={12} className="animate-spin" /> : <Download size={12} />} Export CSV
              </button>
              {filtering && <Loader2 size={14} className="animate-spin text-slate-400" data-testid="hot-loans-filtering" />}
            </div>
          )}
        </div>
        {hot.length === 0 ? (
          filtered
            ? <div className="text-slate-500 py-6" data-testid="hot-loans-no-match">No hot loans match these filters.</div>
            : <div className="text-emerald-600 font-semibold flex items-center gap-2 py-6"><CheckCircle2 size={16} /> All loans compliant.</div>
        ) : (
          <>
            <div className="space-y-2">
              {hot.map((l) => (
                <button key={l.loan_file_id} type="button" onClick={() => setSelected(l)} className="w-full text-left flex items-center gap-3 p-3 rounded-lg border border-slate-100 hover:bg-slate-50 transition" data-testid={`hot-loan-${l.loan_number}`}>
                  <AlertTriangle size={16} style={{ color: '#dc2626' }} />
                  <div className="flex-1">
                    <div className="text-sm font-semibold text-brand-navy">{l.loan_number} · {l.borrower_name || 'No borrower name'}</div>
                    <div className="mt-1"><LoanStatusChip status={l.status} /></div>
                  </div>
                  <div className="flex gap-2">
                    {l.fail_count > 0 && <span className="text-xs px-2 py-1 rounded font-bold bg-red-50 text-red-700">{l.fail_count} fail</span>}
                    {l.warn_count > 0 && <span className="text-xs px-2 py-1 rounded font-bold bg-amber-50 text-amber-700">{l.warn_count} warn</span>}
                  </div>
                </button>
              ))}
            </div>
            <div className="flex items-center justify-between gap-3 mt-3">
              <div className="text-xs text-slate-500" data-testid="hot-loans-showing">Showing {hot.length} of {dash.hot_loans_total}</div>
              {hot.length < dash.hot_loans_total && (
                <button type="button" onClick={loadMore} disabled={loadingMore} className="text-xs font-semibold px-3 py-2 rounded-lg border border-slate-200 inline-flex items-center gap-1.5 disabled:opacity-50" data-testid="hot-loans-more">
                  {loadingMore ? <Loader2 size={12} className="animate-spin" /> : null} Load more
                </button>
              )}
            </div>
          </>
        )}
      </div>

      {selected && <LoanChecksDialog loan={selected} onClose={() => setSelected(null)} />}
    </PageContainer>
  );
}

function LoanChecksDialog({ loan, onClose }) {
  const [checks, setChecks] = useState(null);
  const [failed, setFailed] = useState(false);
  useEffect(() => {
    let live = true;
    losApi.loanChecks(loan.loan_file_id)
      .then((c) => { if (live) setChecks(c); })
      .catch((e) => {
        if (!live) return;
        setFailed(true);
        toast({ title: "Couldn't load checks", description: errorMessage(e) });
      });
    return () => { live = false; };
  }, [loan.loan_file_id]);

  const findings = checks?.findings || [];
  const noTrid = checks && !findings.some((f) => TRID_RULES.has(f.rule));

  return (
    <DialogShell
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      size="lg"
      title={`Checks for ${loan.loan_number}`}
      description={loan.borrower_name || 'No borrower name'}
      data-testid="loan-checks-dialog"
      footer={<Link to={`/portal/loans/${loan.loan_file_id}`} className="text-sm font-semibold text-brand-navy underline" data-testid="open-loan-file">Open loan file</Link>}
    >
      {failed ? (
        <div className="text-sm text-red-600 flex items-center gap-2"><AlertTriangle size={16} /> Couldn't load the checks for this loan.</div>
      ) : !checks ? (
        <div className="text-sm text-slate-500 flex items-center gap-2"><Loader2 className="animate-spin" size={16} /> Loading checks…</div>
      ) : (
        <div className="space-y-2">
          {noTrid && <div className="text-xs text-slate-500" data-testid="no-trid-note">No TRID timing checks ran: the loan file has no application date, and no CD and closing dates.</div>}
          <ChecksList findings={findings} />
        </div>
      )}
    </DialogShell>
  );
}

function Stat({ label, value, color }) {
  return (
    <div className="bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
      <div className="text-xs uppercase tracking-widest text-slate-500">{label}</div>
      <div className="font-serif text-3xl font-bold mt-1" style={{ color }}>{value}</div>
    </div>
  );
}
